import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Box, 
  Typography, 
  Paper, 
  Chip, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  People,
  TrendingUp,
  Coffee,
  Search,
  Public
} from '@mui/icons-material';
import axios from 'axios';

// Fix for default markers
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface CityStats {
  cityName: string;
  totalUsers: number;
  activeUsers: number;
  usersInCafes: number;
  searchingUsers: number;
  coordinates: [number, number];
}

interface CityStatsResponse {
  success: boolean;
  data: CityStats[];
  totalCities: number;
  totalUsers: number;
  totalActiveUsers: number;
  totalUsersInCafes: number;
  totalSearchingUsers: number;
}

const LocationMap: React.FC = () => {
  const [cityStats, setCityStats] = useState<CityStats[]>([]);
  const [timeRange, setTimeRange] = useState<string>('24h');
  const [isLoading, setIsLoading] = useState(true);
  const [summaryStats, setSummaryStats] = useState({
    totalCities: 0,
    totalUsers: 0,
    totalActiveUsers: 0,
    totalUsersInCafes: 0,
    totalSearchingUsers: 0
  });

  // Turkey center coordinates
  const defaultCenter: [number, number] = [39.9334, 32.8597];
  const defaultZoom = 6;

  const fetchCityStats = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get<CityStatsResponse>(`https://api.sobcof.com/api/locations/city-stats`, {
        params: {
          timeRange
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setCityStats(response.data.data);
        setSummaryStats({
          totalCities: response.data.totalCities,
          totalUsers: response.data.totalUsers,
          totalActiveUsers: response.data.totalActiveUsers,
          totalUsersInCafes: response.data.totalUsersInCafes,
          totalSearchingUsers: response.data.totalSearchingUsers
        });
      }
    } catch (error) {
      console.error('Error fetching city stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchCityStats();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      fetchCityStats();
    }, 300000);

    return () => clearInterval(interval);
  }, [fetchCityStats]);

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };

  const getCityColor = (city: CityStats) => {
    if (city.totalUsers > 100) return '#e74c3c'; // Red for high activity
    if (city.totalUsers > 50) return '#f39c12';  // Orange for medium activity
    if (city.totalUsers > 20) return '#f1c40f';  // Yellow for low activity
    return '#95a5a6'; // Gray for very low activity
  };

  const getCityIcon = (city: CityStats) => {
    if (city.usersInCafes > 10) return 'C'; // Coffee for cafe activity
    if (city.searchingUsers > 5) return 'H'; // Heart for match searching
    return 'P'; // People for general activity
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Public />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Şehir İstatistikleri
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Kullanıcı dağılımı ve aktivite haritası (Gizlilik korumalı)
              </Typography>
            </Box>
          </Box>
          
          <FormControl size="medium" sx={{ minWidth: 150 }}>
            <InputLabel>Zaman Aralığı</InputLabel>
            <Select
              value={timeRange}
              label="Zaman Aralığı"
              onChange={handleTimeRangeChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="1h">Son 1 Saat</MenuItem>
              <MenuItem value="6h">Son 6 Saat</MenuItem>
              <MenuItem value="24h">Son 24 Saat</MenuItem>
              <MenuItem value="7d">Son 7 Gün</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Summary Statistics */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 3, 
          mb: 3,
          justifyContent: 'center'
        }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{ 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <Public sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {summaryStats.totalCities}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Aktif Şehir
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{ 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <People sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {summaryStats.totalUsers}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Toplam Kullanıcı
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{ 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {summaryStats.totalActiveUsers}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Aktif Kullanıcı
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{ 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #DEB887 0%, #F4A460 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <Coffee sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {summaryStats.totalUsersInCafes}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Kafe İçinde
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{ 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #F4A460 0%, #DEB887 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <Search sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {summaryStats.totalSearchingUsers}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Eşleşme Arıyor
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Paper>

      {/* Map Container */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {/* City Markers */}
          {cityStats.map((city) => (
            <Marker
              key={city.cityName}
              position={city.coordinates}
              icon={new Icon({
                iconUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
                  <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="18" fill="${getCityColor(city)}" stroke="white" stroke-width="3"/>
                    <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="bold">${getCityIcon(city)}</text>
                  </svg>
                `)}`,
                iconSize: [40, 40],
                iconAnchor: [20, 20],
                popupAnchor: [0, -20]
              })}
            >
              <Popup>
                <Box sx={{ minWidth: 250, p: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                    {city.cityName}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">Toplam Kullanıcı:</Typography>
                      </Box>
                      <Chip 
                        label={city.totalUsers} 
                        size="small" 
                        color="primary" 
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2">Aktif:</Typography>
                      </Box>
                      <Chip 
                        label={city.activeUsers} 
                        size="small" 
                        color="success" 
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Coffee sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="body2">Kafe İçinde:</Typography>
                      </Box>
                      <Chip 
                        label={city.usersInCafes} 
                        size="small" 
                        color="warning" 
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Search sx={{ fontSize: 16, color: 'info.main' }} />
                        <Typography variant="body2">Eşleşme Arıyor:</Typography>
                      </Box>
                      <Chip 
                        label={city.searchingUsers} 
                        size="small" 
                        color="info" 
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Popup>
            </Marker>
          ))}

          {/* Activity Circles */}
          {cityStats.map((city) => (
            <Circle
              key={`circle-${city.cityName}`}
              center={city.coordinates}
              radius={city.totalUsers * 2000} // Scale radius based on user count
              pathOptions={{
                color: getCityColor(city),
                fillColor: getCityColor(city),
                fillOpacity: 0.1,
                weight: 2
              }}
            />
          ))}
        </MapContainer>

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                Harita güncelleniyor...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Şehir istatistikleri yükleniyor
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LocationMap;